/**
 * Flag: localStorage udv.voiceNotes.liveStt = 'true' reactiva Web Speech (experimento).
 * Por defecto OFF: el live primario es Whisper+VAD local.
 *
 * Nota: Chrome suele fallar con `audio-capture` si MediaRecorder ya tiene el mic;
 * por eso hay reintentos y conviene arrancar el STT justo después del recorder.
 */
const FEATURE_FLAG_KEY = 'udv.voiceNotes.liveStt'

/**
 * @returns {boolean}
 */
export function isLiveSpeechSupported() {
  try {
    return !!(
      typeof window !== 'undefined' &&
      (window.SpeechRecognition || window.webkitSpeechRecognition)
    )
  } catch (e) {
    return false
  }
}

/**
 * @returns {boolean}
 */
export function isSecureLiveSpeechContext() {
  try {
    if (typeof window === 'undefined') return false
    if (window.isSecureContext) return true
    const h = window.location && window.location.hostname
    return h === 'localhost' || h === '127.0.0.1'
  } catch (e) {
    return false
  }
}

/**
 * @returns {boolean}
 */
export function isLiveSpeechEnabled() {
  try {
    if (typeof localStorage === 'undefined') return false
    const v = localStorage.getItem(FEATURE_FLAG_KEY)
    // Default OFF (live local Whisper+VAD es el primario)
    if (v == null || v === '') return false
    return v === '1' || v === 'true' || v === 'on'
  } catch (e) {
    return false
  }
}

/**
 * @returns {boolean}
 */
export function canRunLiveSpeech() {
  return (
    isLiveSpeechEnabled() &&
    isLiveSpeechSupported() &&
    isSecureLiveSpeechContext()
  )
}

/**
 * Motivo legible si no se puede usar live STT.
 * @returns {string|null}
 */
export function getLiveSpeechBlockReason() {
  if (!isLiveSpeechEnabled()) return 'flag_off'
  if (!isLiveSpeechSupported()) return 'unsupported_browser'
  if (!isSecureLiveSpeechContext()) return 'insecure_context'
  return null
}

/**
 * Sesión continua con interim + finales.
 */
export class LiveSpeechSession {
  /**
   * @param {{
   *   lang?: string,
   *   onUpdate?: (snap: {
   *     finalText: string,
   *     interimText: string,
   *     text: string,
   *     status: 'listening'|'paused'|'error'|'idle',
   *     error?: string|null,
   *   }) => void,
   * }} [opts]
   */
  constructor(opts = {}) {
    this.lang = opts.lang || 'es-ES'
    this.onUpdate = opts.onUpdate || null
    /** @type {string[]} */
    this._finals = []
    this._interim = ''
    this._wantRunning = false
    this._paused = false
    this._status = 'idle'
    this._error = null
    this._retries = 0
    this._retryTimer = null
    /** Fallo definitivo (red / permiso): no reintentar en esta sesión */
    this._fatal = false
    /** @type {SpeechRecognition|null} */
    this._rec = null
  }

  /**
   * @returns {{
   *   finalText: string,
   *   interimText: string,
   *   text: string,
   *   status: string,
   *   error: string|null,
   * }}
   */
  getSnapshot() {
    const finalText = this._finals.join(' ').replace(/\s+/g, ' ').trim()
    const interimText = String(this._interim || '').trim()
    const text = [finalText, interimText].filter(Boolean).join(' ').trim()
    return {
      finalText,
      interimText,
      text,
      status: this._status,
      error: this._error,
    }
  }

  _emit() {
    if (!this.onUpdate) return
    try {
      this.onUpdate(this.getSnapshot())
    } catch (e) {
      /* ignore */
    }
  }

  _setStatus(status, error = null) {
    this._status = status
    this._error = error
    this._emit()
  }

  _clearRetry() {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer)
      this._retryTimer = null
    }
  }

  _scheduleRestart(delayMs = 400) {
    this._clearRetry()
    if (this._fatal || !this._wantRunning || this._paused) return
    if (this._retries >= 8) {
      this._fatal = true
      this._wantRunning = false
      this._setStatus(
        'error',
        'No se pudo capturar el micrófono para STT en vivo'
      )
      return
    }
    this._retries += 1
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null
      if (this._fatal || !this._wantRunning || this._paused) return
      this._bootRecognition(true)
    }, delayMs)
  }

  _createRecognition() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) throw new Error('Web Speech API no disponible')
    const rec = new SR()
    rec.lang = this.lang
    rec.continuous = true
    rec.interimResults = true
    rec.maxAlternatives = 1

    rec.onstart = () => {
      this._retries = 0
      this._error = null
      this._status = 'listening'
      this._emit()
      try {
        console.info('[UDV] live speech listening')
      } catch (e) {
        /* ignore */
      }
    }

    rec.onresult = (ev) => {
      let interim = ''
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i]
        const piece =
          res && res[0] && res[0].transcript
            ? String(res[0].transcript)
            : ''
        if (!piece) continue
        if (res.isFinal) {
          this._finals.push(piece.trim())
        } else {
          interim += piece
        }
      }
      this._interim = interim
      this._status = 'listening'
      this._error = null
      this._emit()
    }

    rec.onerror = (ev) => {
      const code = (ev && ev.error) || 'error'
      try {
        console.warn('[UDV] live speech error', code)
      } catch (e) {
        /* ignore */
      }
      // Silencios / abort normales
      if (code === 'no-speech' || code === 'aborted') {
        return
      }
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        this._fatal = true
        this._wantRunning = false
        this._clearRetry()
        this._setStatus('error', 'Permiso de dictado denegado')
        return
      }
      // Chrome: el dictado va a servidores de Google. Sin salida a esa red → "network".
      // No spamear reintentos: un intento extra y listo.
      if (code === 'network') {
        if (this._retries < 1) {
          this._setStatus(
            'error',
            'Sin acceso al dictado en la nube — reintentando…'
          )
          this._scheduleRestart(1500)
          return
        }
        this._fatal = true
        this._wantRunning = false
        this._clearRetry()
        this._setStatus(
          'error',
          'Dictado en vivo: usá Google Chrome (Brave/Chromium suelen fallar con “network”). Whisper local al cortar sigue OK.'
        )
        return
      }
      if (code === 'audio-capture') {
        this._setStatus('error', 'Micrófono ocupado — reintentando…')
        this._scheduleRestart(700)
        return
      }
      this._setStatus('error', `STT: ${code}`)
      this._scheduleRestart(800)
    }

    rec.onend = () => {
      if (this._fatal || !this._wantRunning || this._paused) return
      // Chrome corta la sesión al fin de frase: reiniciar
      this._scheduleRestart(120)
    }

    return rec
  }

  /**
   * @param {boolean} [isRetry]
   * @returns {boolean}
   */
  _bootRecognition(isRetry = false) {
    try {
      if (this._rec) {
        try {
          this._rec.onend = null
          this._rec.onerror = null
          this._rec.onresult = null
          this._rec.onstart = null
          this._rec.stop()
        } catch (e) {
          /* ignore */
        }
        this._rec = null
      }
      this._rec = this._createRecognition()
      this._rec.start()
      if (!isRetry) {
        this._setStatus('listening', null)
      }
      return true
    } catch (e) {
      try {
        console.warn('[UDV] live speech start failed', e)
      } catch (e2) {
        /* ignore */
      }
      this._scheduleRestart(500)
      return false
    }
  }

  /**
   * @param {{ delayMs?: number }} [opts]
   * @returns {boolean}
   */
  start(opts = {}) {
    if (!canRunLiveSpeech()) {
      const reason = getLiveSpeechBlockReason()
      this._setStatus(
        'error',
        reason === 'unsupported_browser'
          ? 'Dictado en vivo no soportado (usá Chrome/Edge)'
          : reason === 'insecure_context'
            ? 'Dictado en vivo requiere HTTPS'
            : 'Dictado en vivo desactivado'
      )
      return false
    }
    this._wantRunning = true
    this._paused = false
    this._finals = []
    this._interim = ''
    this._retries = 0
    this._fatal = false
    this._clearRetry()
    this._setStatus('listening', null)

    const delay = Number(opts.delayMs)
    if (delay > 0) {
      this._retryTimer = setTimeout(() => {
        this._retryTimer = null
        if (!this._wantRunning || this._paused) return
        this._bootRecognition(false)
      }, delay)
      return true
    }
    return this._bootRecognition(false)
  }

  /**
   * Reintenta el reconocimiento (p. ej. tras abrir MediaRecorder).
   */
  kick() {
    if (this._fatal || !this._wantRunning || this._paused) return false
    this._retries = Math.min(this._retries, 2)
    return this._bootRecognition(true)
  }

  pause() {
    this._paused = true
    this._wantRunning = false
    this._clearRetry()
    if (this._interim) {
      this._finals.push(this._interim.trim())
      this._interim = ''
    }
    try {
      if (this._rec) {
        this._rec.onend = null
        this._rec.stop()
      }
    } catch (e) {
      /* ignore */
    }
    this._setStatus('paused', null)
  }

  resume() {
    if (!canRunLiveSpeech()) return false
    this._paused = false
    this._wantRunning = true
    this._retries = 0
    this._setStatus('listening', null)
    return this._bootRecognition(false)
  }

  /**
   * Detiene y devuelve el texto acumulado (finales + interim).
   * @returns {string}
   */
  stop() {
    this._wantRunning = false
    this._paused = false
    this._clearRetry()
    if (this._interim) {
      this._finals.push(this._interim.trim())
      this._interim = ''
    }
    const text = this.getSnapshot().finalText
    try {
      if (this._rec) {
        this._rec.onend = null
        this._rec.onresult = null
        this._rec.onerror = null
        this._rec.onstart = null
        this._rec.stop()
      }
    } catch (e) {
      /* ignore */
    }
    this._rec = null
    this._status = 'idle'
    return text
  }
}

export default LiveSpeechSession
